/**
 * useBulkEmailPartners — E-93 Story 93.1 AC-6.
 *
 * TanStack Query wrapper around `fetchEligiblePartners`. Kept separate from
 * `use-partners.ts` because the response shape, filter set, and pagination
 * model (cursor vs. page) all differ — merging would force a lowest-common-
 * denominator DTO that neither consumer wants.
 *
 * Query key includes every filter dimension + cursor so changing any one
 * refetches in-place (no manual invalidation needed).
 */

import {
  fetchEligiblePartners,
  type EligiblePartnerFilters,
  type EligiblePartnerListResponse,
} from '@/api/bulk-email';
import { useQuery } from '@tanstack/react-query';

export interface UseBulkEmailPartnersOptions {
  /** When false the query is parked — handy for gating on feature-flag loads. */
  enabled?: boolean | undefined;
}

export interface UseBulkEmailPartnersResult {
  data: EligiblePartnerListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
}

/**
 * Derive a stable query key from filters. Undefined values are stripped so
 * React does not refetch when the same "empty" filter is re-created by a
 * parent re-render. Order-stable because we serialize via Object.keys().sort().
 */
export function buildQueryKey(filters: EligiblePartnerFilters): unknown[] {
  const normalized: Record<string, unknown> = {};
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key as keyof EligiblePartnerFilters];
    if (value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (typeof value === 'string' && value.length === 0) continue;
    normalized[key] = value;
  }
  return ['bulk-email-eligible-partners', normalized];
}

export function useBulkEmailPartners(
  filters: EligiblePartnerFilters,
  options: UseBulkEmailPartnersOptions = {}
): UseBulkEmailPartnersResult {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: buildQueryKey(filters),
    queryFn: () => fetchEligiblePartners(filters),
    enabled,
    // 10s: quick enough that the user perceives filter changes as "live",
    // long enough to avoid hammering the DB on a double-click of Reset.
    staleTime: 10_000,
    // keepPreviousData removed in v5; rely on the query cache for fast
    // filter-switching UX. If the UX team complains about flicker we can
    // add `placeholderData: (prev) => prev` here.
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading && enabled,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
}
